/**
 * FilterService — manages filter state and merges with customParams
 */

export default class FilterService {
  /**
   * @param {import('../core/StateManager.js').default} stateManager
   * @param {import('../core/EventBus.js').default} eventBus
   * @param {import('../core/Config.js').default} config
   */
  constructor(stateManager, eventBus, config) {
    this._stateManager = stateManager;
    this._eventBus = eventBus;
    this._config = config;
  }

  /**
   * Set one or more filter values
   * @param {Object} filterUpdate - e.g. { gender_ids: [1], colors: ['Red'] }
   */
  setFilter(filterUpdate) {
    this._stateManager.setState({
      filters: filterUpdate,
    });
  }

  /**
   * Reset all user-set filters to defaults
   */
  resetFilters() {
    this._stateManager.setState({
      filters: {
        category_ids: [],
        gender_ids: [],
        colors: [],
        sizes: [],
        price_min: null,
        price_max: null,
      },
    });
  }

  /**
   * Build the complete filter params object for API requests.
   * Merges user-selected filters with customParams from config.
   * User filters take precedence over customParams when non-empty.
   * @returns {Object}
   */
  getFilterParams() {
    const state = this._stateManager.getState();
    const userFilters = state.filters || {};
    const custom = this._config.customParams || {};

    const params = {};

    // category_ids
    const categoryIds = userFilters.category_ids && userFilters.category_ids.length
      ? userFilters.category_ids
      : custom.category_ids || null;
    if (categoryIds && categoryIds.length) {
      params.category_ids = categoryIds;
    }

    // gender_ids: user selection > customParams
    const genderIds = userFilters.gender_ids && userFilters.gender_ids.length
      ? userFilters.gender_ids
      : custom.gender_ids || null;
    if (genderIds && genderIds.length) {
      params.gender_ids = genderIds;
    }

    // colors
    const colors = userFilters.colors && userFilters.colors.length
      ? userFilters.colors
      : custom.colors || null;
    if (colors && colors.length) {
      params.colors = colors;
    }

    // sizes
    const sizes = userFilters.sizes && userFilters.sizes.length
      ? userFilters.sizes
      : custom.sizes || null;
    if (sizes && sizes.length) {
      params.sizes = sizes;
    }

    // price_min
    const priceMin = userFilters.price_min != null ? userFilters.price_min : (custom.price_min || null);
    if (priceMin != null) {
      params.price_min = priceMin;
    }

    // price_max
    const priceMax = userFilters.price_max != null ? userFilters.price_max : (custom.price_max || null);
    if (priceMax != null) {
      params.price_max = priceMax;
    }

    return params;
  }
}
